import type { CSSProperties } from "react";

// Single source of truth for state-pill styling so the leaderboard and the
// per-symbol page render the same colors for the same state value.

type StateLike = string | null | undefined;

const STATE_COLORS: Record<string, { bg: string; fg: string }> = {
  "live": { bg: "#14532d", fg: "#86efac" },        // green-ish — real money
  "paper-live": { bg: "#1e3a8a", fg: "#93c5fd" },  // blue — paper trading
  "retired": { bg: "#4a044e", fg: "#d8b4fe" },     // purple — demoted
};

const FALLBACK = { bg: "#1f2937", fg: "#94a3b8" };

function pillStyle(state: StateLike, active: boolean): CSSProperties {
  const colors = STATE_COLORS[String(state ?? "").toLowerCase()] ?? FALLBACK;
  return {
    display: "inline-block",
    padding: "1px 6px",
    borderRadius: 3,
    background: colors.bg,
    color: colors.fg,
    fontSize: 10,
    fontWeight: 600,
    verticalAlign: "middle",
    border: active ? "1px solid #fbbf24" : "1px solid transparent",
    marginLeft: 6,
  };
}

export function StatePill({
  state,
  active = false,
}: {
  state: StateLike;
  active?: boolean;
}) {
  if (!state) {
    return (
      <span style={pillStyle(undefined, false)} title="No TradingModels entry — likely a backfilled or deleted model.">
        ?
      </span>
    );
  }
  const label = active ? `${state} ★` : state;
  return (
    <span
      style={pillStyle(state, active)}
      title={active ? `${state} — currently in the active ensemble` : String(state)}
    >
      {label}
    </span>
  );
}

// Ordered list of states we surface in the filter dropdown. Order matters
// because "live" is the most actionable and should be at the top.
export const STATE_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "All states" },
  { value: "live", label: "Live (real money)" },
  { value: "paper-live", label: "Paper-live" },
  { value: "paper-live-active", label: "Paper-live (active ensemble only)" },
  { value: "retired", label: "Retired" },
  { value: "_unknown", label: "Unknown / no entry" },
];

export function matchesStateFilter(
  filter: string,
  state: StateLike,
  active: boolean,
): boolean {
  if (!filter) return true;
  if (filter === "paper-live-active") {
    return String(state ?? "").toLowerCase() === "paper-live" && active;
  }
  if (filter === "_unknown") {
    return state === null || state === undefined;
  }
  return String(state ?? "").toLowerCase() === filter;
}
